"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  PersistQueryClientProvider,
  type Persister,
} from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { isBrowser } from "@/lib/platform";
import { createHyredQueryClient } from "@/lib/react-query/queryClient";

const storageKey = "react-query-cache";
const CACHE_MAX_AGE = 1000 * 60 * 60;
const PERSIST_THROTTLE_MS = 1000;

export function ReactQueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => createHyredQueryClient());
  const [persister, setPersister] = useState<Persister | null>(null);

  useEffect(() => {
    if (!isBrowser()) {
      return;
    }

    const storage = window.localStorage;
    const syncPersister = createSyncStoragePersister({
      storage,
      key: storageKey,
      throttleTime: PERSIST_THROTTLE_MS,
    });
    setPersister(syncPersister);
  }, []);

  const content = useMemo(
    () => (
      <>
        {children}
        {process.env.NODE_ENV === "development" ? (
          <ReactQueryDevtools initialIsOpen={false} />
        ) : null}
      </>
    ),
    [children],
  );

  if (!persister) {
    return (
      <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: CACHE_MAX_AGE,
      }}
    >
      {content}
    </PersistQueryClientProvider>
  );
}
